import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { Gender } from '../../../api/app-types/gender.app';
import { CreateInstitutionPerson, InstitutionBranch } from '../../../api/app-types/institution.app';
import { PersonType } from '../../../api/app-types/person-type.app';
import { ClarificationValue } from '../../../api/app-types/relative.app';
import { INSTITUTION_SERVICE } from '../../../api/services/institution.service';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrDataProtection } from '../../../core/components/data-protection/data-protection';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { hasError } from '../../../shared/helpers/has-error.func';
import { wrapPromiseWithNotify } from '../../../shared/helpers/notify.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { maxStringLength } from '../../../shared/validators/length.validator';
import { CreateAddressBody } from '../../../api/app-types/address.app';
import { LvrResponsibility } from '../institution-person-edit/responsibility/responsibility';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';
import { LvrSearchableSelect } from '../../../core/components/searchable-select/searchable-select';

@Component({
	selector: 'lvr-institution-person-create',
	templateUrl: './institution-person-create.html',
	styleUrl: './institution-person-create.scss',
	imports: [
		TranslocoPipe,
		LvrPaddingDirective,
		ReactiveFormsModule,
		LvrAutoSaveDirective,
		LvrInputDirective,
		LvrField,
		MatSelectModule,
		MatFormFieldModule,
		MatInput,
		MatInputModule,
		MatTooltip,
		AddressCompletion,
		LvrContactPoint,
		MatIcon,
		MatButtonModule,
		LvrRestoreActions,
		LvrAppendAsteriskPipe,
		LvrDataProtection,
		LvrDefaultActions,
		LvrResponsibility,
		LvrButton,
		LvrSearchableSelect,
	],
})
export class InstitutionPersonCreate {
	private readonly fb = inject(FormBuilder);
	private readonly env = inject(ENVIRONMENT);
	private readonly persistentStorage = inject(FormPersistenceService);
	private readonly router = inject(Router);
	private readonly api = inject(INSTITUTION_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	private readonly data = toSignal(inject(ActivatedRoute).data);
	private readonly personTypeResponse = computed(() => this.data()?.['personTypes'] as PaginatedResponse<PersonType>);
	private readonly institutionId = toSignal(inject(ActivatedRoute).paramMap.pipe(map((pm) => Number(pm.get('id')))));

	public readonly branchesPage = computed(() => this.data()?.['branches'] as PaginatedResponse<InstitutionBranch>);
	public readonly branches = computed(() => this.branchesPage().entities ?? []);
	public readonly personTypes = computed(() => (this.personTypeResponse().entities ?? []) as PersonType[]);
	public readonly personTypeLabel = (personType: PersonType): string => personType.name;
	public readonly contactPointCategories = computed(
		() => (this.data()?.['contactPointCategories']?.entities ?? []) as ContactPointCategory[],
	);
	public readonly saving = signal(false);
	public readonly genders = computed(() => (this.data()?.['genders'] ?? []) as Gender[]);
	public readonly draftId = FORM_KEYS.CREATE_INSTITUTION_PERSON;
	public readonly form = this.initForm(this.fb);
	public readonly formValues = toSignal(this.form.valueChanges.pipe(map(() => this.form.value)));
	public readonly communicationWayCount = signal(1);
	public readonly communicationWayCountArrayKeys = computed(() => Array(this.communicationWayCount()));
	public readonly remarkCtrlMaxLength = this.env.textareaMaxLength;
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly canCreateInstitutionPersonsPermission = hasPermission(
		this.session.permissions,
		'InstitutionPersons.Create',
	);

	private initForm(fb: FormBuilder) {
		const form = fb.group({
			categoryId: [undefined, [Validators.required]],
			remark: ['', [maxStringLength(this.env.textareaMaxLength)]],
			title: ['', [maxStringLength(50)]],
			genderId: [undefined],
			firstName: ['', [Validators.pattern(this.env.nameRegexStr), maxStringLength(50)]],
			lastName: ['', [Validators.pattern(this.env.nameRegexStr), maxStringLength(50), Validators.required]],
			address: [undefined],
			communicationWays: this.fb.control<ContactPoint[]>([]),
			clarification: this.fb.control<ClarificationValue>({
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			}),
			responsibleBranches: this.fb.control<number[]>([]),
		});

		return form;
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		if (data.valid) {
			const values = ctrl.value ?? [];
			if (index !== -1) {
				values[index] = data.value;
			} else {
				values.push(data.value);
			}
			ctrl.setValue(values);
		} else {
			const values = ctrl.value ?? [];
			if (index !== -1) {
				values.splice(index, 1);
			}
			ctrl.setValue(values);
		}
	}

	public cancel() {
		this.persistentStorage.deleteDraft('institution-person', this.draftId);
		this.router.navigate(['/desk/institution', this.institutionId(), 'persons']);
	}

	public async save() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const EMPTY_ADDRESS: CreateAddressBody = {
			streetAndHouseNumber: '',
			postalCodeAndCity: '',
			soundexStreet: '',
			anonym: '',
			identifier: '',
		};

		this.saving.set(true);

		try {
			const value = this.form.value;

			const clar = value.clarification ?? {
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			};

			const filteredCws = (value.communicationWays ?? []).filter((cw) => cw.id === -1).filter(isContactPointComplete);

			const body: CreateInstitutionPerson = {
				address: value.address ?? EMPTY_ADDRESS,
				categoryId: value.categoryId ?? 0,
				communicationWays: filteredCws.map((cp) => ({
					preferredWayOfContact: cp.preferredWayOfContact,
					categoryContactPointId: cp.categoryContactPointId,
					contactValue: cp.contactValue?.trim(),
					remark: cp.remark?.trim(),
				})),
				firstName: value.firstName ?? '',
				genderId: value.genderId ?? undefined,
				institutionId: this.institutionId()!,
				lastName: value.lastName ?? '',
				remark: value.remark ?? '',
				title: value.title ?? '',
				hasBeenClarified: clar.hasBeenClarified,
				clarifiedAt: clar.clarifiedAt,
				typeOfClarification: clar.typeOfClarification,
				responsibleBranchIds: value.responsibleBranches ?? [],
			};

			const result = await wrapPromiseWithNotify(
				this.api.createPerson(body),
				this.transloco.translate('base.notification_message.institution_person_created'),
				this.ntfy,
				this.transloco.translate('base.notification_message.institution_person_create_error'),
			);

			this.persistentStorage.deleteDraft('institution-person', this.draftId);
			await this.router.navigate(['/desk/institution', this.institutionId(), 'persons', result.id]);
		} finally {
			this.saving.set(false);
		}
	}

	public addCommunicationWay() {
		const vals = this.form.get('communicationWays')?.value ?? [];
		vals.push({
			id: -1,
			personId: -1,
			personTypeId: -1,
			categoryContactPointId: -1,
			contactValue: '',
			preferredWayOfContact: false,
			remark: '',
		});
		this.form.get('communicationWays')?.setValue(vals);
		this.communicationWayCount.set(this.communicationWayCount() + 1);
	}

	public removeCommunicationWay(id: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const index = values.findIndex((v) => v.id === id);
		if (index !== -1) {
			values.splice(index, 1);
			ctrl.setValue(values);
		}
		this.communicationWayCount.set(this.communicationWayCount() - 1);
	}
}
